const WAITLIST_ROUTE = '/api/joinWaitlist';
const DEFAULT_FIREBASE_PROJECT_ID = 'thefireapp-a0557';
const DEFAULT_FUNCTION_REGION = 'us-central1';

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const getEnvValue = (key: string): string | undefined => {
  const nodeValue = typeof process !== 'undefined' ? process.env?.[key] : undefined;
  if (typeof nodeValue === 'string' && nodeValue.trim()) return nodeValue.trim();

  return undefined;
};

const getCurrentOriginRoute = () => {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}${WAITLIST_ROUTE}`;
};

const uniqueEndpoints = (endpoints: Array<string | undefined>) => {
  const seen = new Set<string>();
  return endpoints.flatMap((endpoint) => {
    if (!endpoint) return [];
    const normalized = trimTrailingSlash(endpoint);
    if (!normalized || seen.has(normalized)) return [];
    seen.add(normalized);
    return [normalized];
  });
};

const getWaitlistEndpoints = () => {
  const configuredEndpoint = getEnvValue('VITE_WAITLIST_ENDPOINT_URL');
  const projectId = getEnvValue('VITE_FIREBASE_PROJECT_ID') || DEFAULT_FIREBASE_PROJECT_ID;
  const directFunctionEndpoint = `https://${DEFAULT_FUNCTION_REGION}-${projectId}.cloudfunctions.net/joinWaitlist`;
  const currentOriginRoute = getCurrentOriginRoute();

  return uniqueEndpoints([
    directFunctionEndpoint,
    configuredEndpoint,
    currentOriginRoute,
    WAITLIST_ROUTE,
  ]);
};

const isSuccessfulWaitlistResponse = async (response: Response) => {
  if (!response.ok) return false;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) return false;

  try {
    const body: unknown = await response.json();
    return typeof body === 'object' && body !== null && (body as { ok?: unknown }).ok === true;
  } catch {
    return false;
  }
};

export const joinWaitlist = async (email: string) => {
  const payload = JSON.stringify({ email });
  const endpoints = getWaitlistEndpoints();
  let lastError: unknown;

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });

      if (await isSuccessfulWaitlistResponse(response)) return;

      lastError = new Error(`Waitlist request failed with ${response.status}`);
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError instanceof Error) {
    console.error('[waitlist] request failed', lastError);
  }

  throw new Error('We couldn’t join the waitlist right now. Please try again soon.');
};
